import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const CoalHarbour = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.coal-harbour-vancouver:About Coal Harbour Paragraph">
          Coal Harbour, nestled between Vancouver’s bustling downtown and the
          expansive Stanley Park, is a picturesque waterfront neighborhood known
          for its upscale residences, scenic marina, and stunning mountain
          views. This area seamlessly blends urban sophistication with natural
          beauty, offering residents and visitors a tranquil yet vibrant
          environment. The neighborhood is characterized by modern high-rise
          condominiums, luxury hotels, and a variety of dining establishments,
          all set against the backdrop of the North Shore Mountains and Burrard
          Inlet.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,123 - $2,523',
          bed2: '$2,813 - $3,213',
          bed3: '$5,519 - $5,919'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.coal-harbour-vancouver:Apartment Hunting Coal Harbour Paragraph 1">
            Finding an apartment for rent in Coal Harbour, Vancouver, BC
            requires careful planning and consideration. Start by determining
            your criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Coal Harbour lifestyle by exploring
            your new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.coal-harbour-vancouver:Apartment Hunting Coal Harbour Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Apartments for rent in Coal Harbour Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Coal Harbour, Vancouver
              </LinkText>
              , cater to professionals and families seeking modern living spaces
              with waterfront views. These units range from stylish one-bedroom
              apartments to expansive three-bedroom suites, often featuring
              high-end finishes and amenities. The neighborhood’s proximity to
              downtown Vancouver, coupled with its serene ambiance, makes it a
              highly desirable location for renters. According to recent data,
              the average monthly rent for a one-bedroom apartment in Coal
              Harbour is approximately $2,323, while a two-bedroom unit averages
              around $3,013.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Houses for rent in Coal Harbour Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Coal Harbour, Vancouver
              </LinkText>
              , are relatively rare due to the neighborhood’s high-density urban
              planning, which predominantly features condominiums and apartment
              buildings. However, when available, these properties are ideal for
              those seeking spacious living arrangements with the convenience of
              urban amenities. Residents can enjoy private outdoor spaces and
              proximity to the waterfront, making it a coveted option for
              discerning renters.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Townhouses for rent in Coal Harbour Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Coal Harbour, Vancouver
              </LinkText>{' '}
              , offer a unique blend of condominium convenience and the privacy
              of a standalone home. These multi-level residences are suitable
              for families and professionals desiring additional space without
              compromising on location. Features often include private
              entrances, attached garages, and access to communal facilities.
              Renting a townhouse in Coal Harbour provides a harmonious balance
              between urban living and suburban comfort.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Basements for rent in Coal Harbour Paragraph">
              Basement rentals are uncommon in Coal Harbour due to the
              prevalence of high-rise developments. The neighborhood’s
              architectural landscape is dominated by modern towers, leaving
              limited opportunities for traditional basement suites. Prospective
              renters seeking similar accommodations might consider neighboring
              areas where such options are more readily available.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Condos for rent Coal Harbour Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Coal Harbour, Vancouver
              </LinkText>
              , are highly sought after, offering luxurious living spaces with
              panoramic views of the harbor and mountains. These units often
              come with upscale amenities such as concierge services, fitness
              centers, and swimming pools. The average rent for a two-bedroom
              condo in this area is approximately $3,013 per month, reflecting
              the neighborhood’s premium status.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Rooms for rent in Coal Harbour Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Coal Harbour, Vancouver
              </LinkText>{' '}
              , are ideal for individuals seeking affordable living arrangements
              in a prime location. These rentals typically involve sharing
              common areas like kitchens and living rooms with other tenants.
              Renting a room in Coal Harbour offers an economical way to enjoy
              the neighborhood’s upscale environment and proximity to downtown
              amenities.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.coal-harbour-vancouver:Short term rentals in Coal Harbour Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Coal Harbour, Vancouver
              </LinkText>
              , cater to tourists, business travelers, or individuals in
              transition. These accommodations range from furnished apartments
              to luxury condos, providing flexibility and comfort. Opting for a
              short-term rental in Coal Harbour allows guests to experience the
              neighborhood’s vibrant culture, access to shopping centers, and
              proximity to major city attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.coal-harbour-vancouver:Exploring Coal Harbour Paragraph">
          Coal Harbour is a captivating blend of urban sophistication and
          natural splendor. The neighborhood boasts a scenic seawall, perfect
          for walking, jogging, or cycling, offering uninterrupted views of the
          marina and North Shore Mountains. Notable landmarks include the{' '}
          <LinkText href="https://www.vancouverconventioncentre.com/">
            Vancouver Convention Centre
          </LinkText>
          , with its iconic living roof, and the{' '}
          <LinkText href="https://www.oxfordproperties.com/lease/office/marine-building">
            Marine Building
          </LinkText>
          , a fine example of Art Deco architecture. The area is also home to
          numerous art installations, such as the ’
          <LinkText href="https://www.vancouverconventioncentre.com/about-us/art-project/digital-orca">
            Digital Orca
          </LinkText>
          ’ sculpture by Douglas Coupland, reflecting Vancouver’s cultural
          vibrancy. With its proximity to{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/stanley-park.aspx">
            Stanley Park
          </LinkText>
          , residents and visitors have easy access to expansive green spaces,
          beaches, and recreational facilities.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.coal-harbour-vancouver:Things To Do Coal Harbour Paragraph">
          Vancouver is a vibrant city offering a mix of urban attractions and
          stunning natural scenery.{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/stanley-park.aspx">
            Stanley Park
          </LinkText>{' '}
          is a must-visit, featuring the famous Seawall, scenic trails, and the{' '}
          <LinkText href="https://www.vanaqua.org/">
            Vancouver Aquarium
          </LinkText>
          . For cultural experiences, head to{' '}
          <LinkText href="https://granvilleisland.com/">
            Granville Island
          </LinkText>
          , home to artisan shops, theaters, and the Public Market.{' '}
          <LinkText href="https://gastown.org/">Gastown</LinkText>, Vancouver’s
          historic district, boasts cobblestone streets, the iconic Steam Clock,
          and trendy boutiques. Art lovers can explore the{' '}
          <LinkText href="https://www.vanartgallery.bc.ca/">
            Vancouver Art Gallery
          </LinkText>
          , while science enthusiasts will enjoy{' '}
          <LinkText href="https://www.scienceworld.ca/">Science World</LinkText>
          . Outdoor adventurers can take the{' '}
          <LinkText href="https://www.grousemountain.com/skyride">
            Grouse Mountain Skyride
          </LinkText>{' '}
          for breathtaking city views or visit Capilano Suspension Bridge Park
          for thrilling treetop walks.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.coal-harbour-vancouver:Restaurants Coal Harbour Paragraph">
        <Paragraph>
          Coal Harbour Vancouver offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://mikurestaurant.com/">
            Miku Restaurant
          </LinkText>{' '}
          - renowned for its innovative Aburi (flame-seared) sushi and
          contemporary Japanese cuisine. The restaurant offers a sophisticated
          dining experience with stunning waterfront views. Signature dishes
          include the Aburi Oshi Sushi and Miku’s modern take on traditional
          Japanese desserts. It’s an ideal spot for sushi aficionados and those
          seeking a refined dining atmosphere.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.vancouverdine.com/carderos/">
            Cardero’s Restaurant
          </LinkText>{' '}
          - combines a casual atmosphere with a menu focused on fresh seafood
          and grilled specialties. Located right on the marina, it offers diners
          picturesque views alongside live music. Signature dishes include
          cedar-planked salmon and a variety of wood-fired pizzas. It’s a
          perfect venue for those looking to enjoy a relaxed meal with scenic
          surroundings.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.liftbarandgrill.com/">
            Lift Bar Grill View
          </LinkText>{' '}
          - offers a dynamic dining experience with its multi-level layout,
          including a rooftop patio boasting panoramic views of the harbor and
          mountains. The menu features West Coast cuisine with an emphasis on
          seafood, including sushi offerings.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.coal-harbour-vancouver:Transportation Coal Harbour Paragraph">
          Getting around Coal Harbour, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Coal Harbour are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Coal Harbour offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.coal-harbour-vancouver:Where To Stay Coal Harbour Paragraph">
          Accommodation options in Coal Harbour, Vancouver cater to those
          looking for a more local and immersive experience. This area offers a
          mix of boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Coal Harbour itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Coal
          Harbour allows visitors to experience its distinct character while
          remaining conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default CoalHarbour
